/**
 * @component FaqPage
 * @description 고객 문의 전 자주 묻는 질문을 빠르게 확인할 수 있도록 FAQ 아코디언 목록을 제공하는 페이지입니다.
 *              FAQ 편집은 관리자 콘솔(/admin/faq)에서 수행합니다.
 * @usage App 라우팅에서 `/cs/faq.html` 경로일 때 렌더링됩니다.
 */

import { useState, useEffect } from 'react';
import PageHero from '../../components/common/PageHero';
import SectionHeading from '../../components/common/SectionHeading';
import { pageMeta } from '../../constants/navigation';
import { useFaqAccordion } from '../../hooks/useFaqAccordion';

export default function FaqPage() {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { expandedFaq, toggleFaq } = useFaqAccordion();

  // [기능] 백엔드 API에서 FAQ 목록을 가져옵니다.
  useEffect(() => {
    let isActive = true;
    fetch('/api/faq')
      .then((r) => r.json())
      .then((items) => {
        if (isActive) setFaqItems(items);
      })
      .catch(() => {
        // [비즈니스] FAQ를 불러오지 못해도 페이지의 나머지 안내는 정상 노출되어야 합니다.
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, []);

  return (
    <>
      <PageHero title={pageMeta.faq.title} trail={pageMeta.faq.trail} className="cs-page-hero" />
      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="FAQ"
            title="자주 묻는 질문을 먼저 확인해보세요"
            description="도입 방식, 지원 범위, 상담 절차 등 자주 문의하시는 내용을 정리했습니다."
            className="fade-in"
          />

          {loading ? (
            <p style={{ color: 'rgba(32,35,50,0.5)', fontSize: '0.95rem' }}>로딩 중...</p>
          ) : (
            <div className="faq-list">
              {faqItems.map((item, index) => {
                const isOpen = expandedFaq === item.question;

                return (
                  <article key={index} className={`faq-card card-panel fade-in ${isOpen ? 'is-open' : ''}`}>
                    <div className="faq-trigger-row">
                      {/* [이벤트 처리]: 질문 버튼 클릭 시 해당 항목의 펼침/접힘 상태를 전환합니다. */}
                      <button
                        type="button"
                        className="faq-trigger"
                        onClick={() => toggleFaq(item.question)}
                        aria-expanded={isOpen}
                        style={{ flex: 1 }}
                      >
                        <span>{item.question}</span>
                        <strong>{isOpen ? '−' : '+'}</strong>
                      </button>
                    </div>
                    <div className={`faq-content ${isOpen ? 'is-open' : ''}`}>
                      <p>{item.answer}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
